import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Define button style variants
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap " +
    "rounded-md text-sm font-medium ring-offset-background transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
    "focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 " +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",

        // Updated glow variant
        glow: [
          "relative bg-gradient-to-r from-red-600 via-blue-600 to-red-600",
          "text-white font-semibold",
          "shadow-lg shadow-blue-500/20",
          "transition-all duration-300 transform",
          "hover:scale-105",
          "hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]",
          "hover:from-red-700 hover:via-blue-700 hover:to-red-700",
          "active:scale-95",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          "after:absolute after:inset-0 after:z-[-1] after:bg-gradient-to-r after:from-red-600/50 after:via-blue-600/50 after:to-red-600/50 after:blur-xl after:opacity-50",
        ].join(" "),
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }